import { useCallback, useEffect, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { CountryData } from '../../../lib/models/enroll';
import { RootState } from '../../../lib/store';
import { getCountryList as getCountryListCall } from '../../../lib/store/crm';
import { CRMCall } from '../../../lib/store/crm/types';
import CountriesCache from '../../../lib/store-util/CountriesCache';
import {
  isCallStatusError,
  isCallStatusReady,
} from '../../../lib/util/error-handling/StatusByCallHelpers';
import {
  getFirstNameValidity,
  getGenericFieldValidity,
  getLastNameValidity,
  validateControl,
} from '../../../lib/util/ValidationSchemes/Validate';

import { CountryControl, InputControl } from '../../../components';
import { ICountryControlOptions } from '../../../components/CountryControl/CountryControl.types';
import {
  GTM_CATEGORIES,
  GTM_CLIENT_CREATION_STEPS,
  GTM_EVENTS,
  GTM_FORM_NAMES,
} from '../../../constants/gtm';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollPersonalInfo.module.scss';

type Props = ReduxProps;
const ADDITIONAL_HEIGHT_PER_ERROR = 24;

const EnrollPersonalInfo = (props: Props): JSX.Element => {
  const { getCountryList, isServicesReady, isCountryListReady, isGetCountryListFailed } = props;
  const isGetCountryListPending = !isCountryListReady && isGetCountryListFailed;
  const [additionalHeight, setAdditionalHeight] = useState(0);
  const countries = CountriesCache.use();
  const { form } = EnrollmentState.use();
  const { firstName, lastName, country } = form;
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const providedPlanId = searchParams.get('providedPlanId');
  const planId = providedPlanId ?? location.state?.id;
  const providedAutoPass = searchParams.get('withAutoPass') === 'true';
  const clientId = searchParams.get('clientId') ?? location.state?.clientId;
  const referralCode = searchParams.get('ref') ?? location.state?.referralCode;
  const { isMobile } = useIsMobile();

  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    if (!countries && !isGetCountryListPending && isServicesReady) {
      getCountryList();
    }
  }, [countries, isServicesReady, isGetCountryListPending, getCountryList]);

  const countryOptions = useMemo(
    (): ICountryControlOptions[] =>
      countries
        ?.filter((countryData: CountryData) => countryData.id && countryData.name)
        .sort((a: CountryData, b: CountryData) => a.name.localeCompare(b.name))
        .map((countryData: CountryData) => ({
          label: countryData.name,
          value: countryData.id,
        })),
    [countries],
  );

  const validateForm = useCallback(() => {
    const countryErrors = validateControl(
      country,
      [],
      lang.commonOnboardingInitialCountryLabel(),
      true,
    ).errors;
    const firstNameErrors = getFirstNameValidity(firstName).errors;
    const lastNameErrors = getLastNameValidity(lastName).errors;
    return {
      countryErrors,
      firstNameErrors,
      lastNameErrors,
      isValid: !countryErrors.length && !firstNameErrors.length && !lastNameErrors.length,
    };
  }, [country, firstName, lastName]);

  const handleSubmit = () => {
    setIsSubmitButtonClicked(true);
    setHasUserInteracted(true);

    const { countryErrors, firstNameErrors, lastNameErrors, isValid } = validateForm();

    if (isValid) {
      // Get the country name from countryOptions
      const selectedCountry = countryOptions?.find((c) => c.value === country);

      // Push GTM event for client creation step 1
      TagManager.dataLayer({
        dataLayer: {
          event: GTM_EVENTS.CLIENT_CREATION,
          event_step: GTM_CLIENT_CREATION_STEPS.STEP_1.step,
          event_category: GTM_CATEGORIES.CLIENT_CREATION_FUNNEL,
          event_action: GTM_CLIENT_CREATION_STEPS.STEP_1.action,
          event_label: GTM_CLIENT_CREATION_STEPS.STEP_1.label,
          form_name: GTM_FORM_NAMES.CREATE_ACETRADE_CLIENT,
          country_selected: selectedCountry?.label || '',
        },
      });

      EnrollmentState.setPersonalInfoStepPassed(true);
      navigate(
        {
          pathname: UI_ROUTES.secondEnroll,
        },
        { state: { id: planId, autoPass: providedAutoPass, clientId, referralCode } },
      );
    }

    // Calculate how many fields have errors, we only display one at a time
    const totalErrorFields = [
      countryErrors.length > 0,
      firstNameErrors.length > 0,
      lastNameErrors.length > 0,
    ].filter(Boolean).length;

    // Update the state with the new additional height
    setAdditionalHeight(totalErrorFields * ADDITIONAL_HEIGHT_PER_ERROR);
  };

  useEffect(() => {
    if (hasUserInteracted) {
      // Only run this if user has interacted
      const { countryErrors, firstNameErrors, lastNameErrors } = validateForm();

      const totalErrorFields = [
        countryErrors.length > 0,
        firstNameErrors.length > 0,
        lastNameErrors.length > 0,
      ].filter(Boolean).length;

      setAdditionalHeight(totalErrorFields * ADDITIONAL_HEIGHT_PER_ERROR);
    }
  }, [country, firstName, lastName, validateForm, hasUserInteracted]);

  return (
    <Layout
      formTitle={lang.commonEnrollmentCreateProfileTitle()}
      {...(isMobile && { formSubtitle: lang.commonEnrollmentCreateProfileSubTitle() })}
      onSubmit={handleSubmit}
      onBackBtnPress={() => {
        EnrollmentState.resetForm();
        navigate(-1);
      }}
      onClose={() => {
        EnrollmentState.resetForm();
        navigate(UI_ROUTES.baseURL);
      }}
      nextButtonText={lang.commonContinueButton()}
      displayFooter
      displayLoginButton
      displayNextButton
      displayBackButton
      additionalHeight={additionalHeight}
    >
      <div
        className={classNames(styles.row, {
          [styles.errorRow]:
            isSubmitButtonClicked
            && validateControl(country, [], lang.commonOnboardingGenericRequiredField(), true).errors
              .length > 0,
        })}
      >
        <span className={styles.title}>{lang.commonOnboardingPhoneVerificationCountry()}</span>

        <CountryControl
          className={styles.input}
          options={countryOptions}
          onSubmit={handleSubmit}
          value={country}
          id="countryResidence"
          ariaLabel={lang.commonOnboardingInitialCountryLabel()}
          placeholder={lang.commonOnboardingInitialCountryLabel()}
          onCountryChange={(countryId: string) => {
            EnrollmentState.setCountry(countryId);
          }}
          errors={
            isSubmitButtonClicked
              ? validateControl(country, [], lang.commonOnboardingGenericRequiredField(), true)
                .errors
              : []
          }
        />
      </div>

      <div
        className={classNames(styles.row, {
          [styles.errorRow]:
            isSubmitButtonClicked && getGenericFieldValidity(firstName).errors.length > 0,
        })}
      >
        <span className={styles.title}>{lang.commonOnboardingPersonalDetailsFirstName()}</span>

        <InputControl
          className={styles.input}
          name=""
          placeholder=""
          onSubmit={handleSubmit}
          label={lang.commonEnrollmentEnterFirstName()}
          value={firstName}
          onValueChange={(value: string | number) => {
            EnrollmentState.setFirstName(String(value));
          }}
          errors={isSubmitButtonClicked ? getFirstNameValidity(firstName).errors : []}
        />
      </div>

      <div
        className={classNames(styles.row, {
          [styles.errorRow]:
            isSubmitButtonClicked && getGenericFieldValidity(lastName).errors.length > 0,
        })}
      >
        <span className={styles.title}>{lang.commonOnboardingPersonalDetailsLastName()}</span>
        <InputControl
          className={styles.input}
          name=""
          placeholder=""
          onSubmit={handleSubmit}
          label={lang.commonEnrollmentEnterLastName()}
          value={lastName}
          onValueChange={(value: string | number) => {
            EnrollmentState.setLastName(String(value));
          }}
          errors={isSubmitButtonClicked ? getLastNameValidity(lastName).errors : []}
        />
      </div>
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  getCountryList: () => dispatch(getCountryListCall()),
});

const mapStateToProps = (state: RootState) => ({
  isServicesReady: state.app.isServicesReady,
  isCountryListReady: isCallStatusReady(CRMCall.getCountryList, state.crm.statusByCall),
  isGetCountryListFailed: isCallStatusError(CRMCall.getCountryList, state.crm.statusByCall),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollPersonalInfo);
